import { useState, useEffect, useRef } from "react";
import "../common/style.css";
import "../../ProjectSurveysV2/style/style.css";
import { Tab, Button, Form, Nav, Row, Col } from "react-bootstrap";
import { useListProjectPhases, useGetProjectById, useListVendors } from "./api";
import { useListProjectServices } from "../common/api";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { faCirclePlus } from "@fortawesome/pro-light-svg-icons";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { useNavigate } from "react-router";
import { ProjectPhase, ProjectService, ProjectSubservice } from "../types";
import {
  useCreateProjectPhaseMutation,
  useUpdateProjectPhaseMutation,
  useDeleteProjectPhaseMutation,
  useUpdateProjectServiceMutation,
  useDeleteProjectServiceMutation,
  useUpdateProjectSubserviceMutation,
  useDeleteProjectSubserviceMutation,
  useCreateProjectResourceMutation,
} from "@generated";
import { TextField } from "@components/FormsV2";
import {
  handleAlert,
  snakeToText,
  renderResourceOptions,
} from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import Select from "react-select";
import {
  useListProjectResources,
  useListResourceRates,
  useListResources,
} from "../../Projects/ServicesV2/api";
import useGetAccount from "../../Account/EditAccount/api/useGetAccount";
import { useListServiceCategoriesForAccount } from "../../Admin/ProfessionalServices/Show/api";
import { ServiceEditorV2 } from "../common/ServiceEditorV2";
import { ServicePreview } from "@components/PreviewSlideouts";
import { Service } from "../../Projects/ServicesV2/types";
import { Option } from "../../Admin/ProfessionalServices/Show/types";
import useListLanguageFields from "../../Admin/ProfessionalServices/common/api/useListLanguageFields";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import MarkdownTextArea from "@components/FormsV2/MarkdownTextArea/MarkdownTextArea";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import AddVendorSlideout from "../../VendorQuotesV2/common/AddVendorSlideout";
import {
  calculatePrice,
  calculateMarkup,
} from "../../VendorQuotesV2/quoteHelpers/quoteHelpers";
import useFlags from "@common/hooks/useFlags";

interface Props {
  account_slug: string;
  service_type: "professional_services" | "managed_services";
}

const Show = ({ account_slug, service_type }: Props) => {
  const navigate = useNavigate();

  // Prevent mousewheel from changing number input values
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement &&
        target.type === "number" &&
        target === document.activeElement
      ) {
        target.blur();
      }
    };
    document.addEventListener("wheel", handleWheel, { passive: true });
    return () => document.removeEventListener("wheel", handleWheel);
  }, []);
  const {
    configVendorQuoteRevenueCalculation,
    configServicePricingEnabled,
    releaseTiptapEditor,
  } = useFlags();
  const { hasPrivilege, privilegesLoading } = usePrivilegeCheck();
  const project_id = window.location.href?.split("/")?.[4];
  const { project, isLoading: projectLoading } = useGetProjectById(
    Number(project_id)
  );
  const { data: account } = useGetAccount(account_slug);
  const {
    fetchedServices,
    servicesLoading,
    servicesFetching,
    refetchServices,
  } = useListProjectServices({
    project_id,
    service_type,
  });
  const { fetchedPhases, phasesLoading, phasesFetching, refetchPhases } =
    useListProjectPhases(project_id);
  const { vendors, refetchVendors } = useListVendors();
  const rateTableId = project?.rateTableId;
  const resourceRatesEnabled =
    account?.attributes?.settings?.project_resource_rates;
  const { projectResources, refetchProjectResources } =
    useListProjectResources(project_id);
  const { resources } = useListResources();
  const { resourceRates } = useListResourceRates(rateTableId);
  const { categories } = useListServiceCategoriesForAccount();
  const { fields } = useListLanguageFields();

  const [showAddServicesModal, setShowAddServicesModal] =
    useState<boolean>(false);
  const [openPhaseMenuId, setOpenPhaseMenuId] = useState<string | null>(null);
  const [showRenamePhaseModal, setShowRenamePhaseModal] =
    useState<boolean>(false);
  const [showDeletePhaseModal, setShowDeletePhaseModal] =
    useState<boolean>(false);
  const [showAddPhaseModal, setShowAddPhaseModal] = useState<boolean>(false);
  type SlideoutView = "addResource" | "addVendor" | null;
  const [slideoutStack, setSlideoutStack] = useState<SlideoutView[]>([]);
  const [showDeleteServicesModal, setShowDeleteServicesModal] =
    useState<boolean>(false);
  const [newPhaseName, setNewPhaseName] = useState<string>("");
  const [newResource, setNewResource] = useState<{
    vendor?: { label: string; value: string };
    name: string;
    cost?: string;
    margin?: string;
    price?: string;
  }>({ name: "", cost: "0.00", margin: "25", price: "0.00" });
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");
  const [serviceToPreview, setServiceToPreview] =
    useState<Service | null>(null);
  const [serviceSlideoutOpen, setServiceSlideoutOpen] =
    useState<boolean>(false);
  const [selectedLanguageField, setSelectedLanguageField] = useState<Option>({
    label: "Service Description",
    value: "service-description",
  });
  const [activeTab, setActiveTab] = useState("1");
  const [createPhase] = useCreateProjectPhaseMutation();
  const [updatePhase] = useUpdateProjectPhaseMutation();
  const [deletePhase] = useDeleteProjectPhaseMutation();
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const prevPhasesRef = useRef<ProjectPhase[]>([]);
  const prevServicesRef = useRef<ProjectService[]>([]);
  const [createVendorResource] = useCreateProjectResourceMutation();
  const vendorResourceLabel =
    account?.attributes?.fieldLabels?.["vendor_resource"] || "Vendor Resource";
  const [services, setServices] = useState<ProjectService[]>([]);
  const hasPendingItems = services.some(
    (s) => s.isPending || s.subservices?.some((sub) => sub.isPending)
  );
  const selectedServices = services.flatMap((service) => [
    ...(service.checked ? [service] : []),
    ...(service.subservices?.filter((s) => s?.checked) ?? []),
  ]);
  const [expanded, setExpanded] = useState({ phases: {}, services: {} });
  const [updateService] = useUpdateProjectServiceMutation();
  const [deleteService] = useDeleteProjectServiceMutation();
  const [updateSubservice] = useUpdateProjectSubserviceMutation();
  const [deleteSubservice] = useDeleteProjectSubserviceMutation();

  useEffect(() => {
    const sorted = [...fetchedPhases].sort((a, b) => a.position - b.position);
    setPhases(sorted);
    prevPhasesRef.current = sorted; // initialize the baseline reference
  }, [phasesLoading, phasesFetching]);

  useEffect(() => {
    if (!servicesLoading && !servicesFetching) {
      setServices(fetchedServices);
      prevServicesRef.current = fetchedServices;
    }
  }, [servicesLoading, servicesFetching]);

  useEffect(() => {
    if (!hasPendingItems) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasPendingItems]);

  const toggleSubservices = (expand) => {
    const services: Record<string, boolean> = {};
    const phases: Record<string, boolean> = {};
    fetchedServices.forEach(
      (s) => (services[s.id] = expand && s?.subservices?.length ? true : false)
    );
    if (expand)
      fetchedPhases.forEach((p) => (phases[p.id] = expand ? true : false));
    setExpanded(expand ? { phases, services } : { ...expanded, services });
  };

  useEffect(() => {
    // initialize phases expanded on mount
    if (!phasesLoading && !servicesLoading) {
      toggleSubservices(true);
      toggleSubservices(false);
    }
  }, [phasesLoading, servicesLoading]);

  const closeModal = () => {
    setNewPhaseName("");
    setNewResource({ name: "" });
    setOpenPhaseMenuId(null);
    setShowAddPhaseModal(false);
    setShowRenamePhaseModal(false);
    setShowDeletePhaseModal(false);
    setShowDeleteServicesModal(false);
  };

  const handleUpdateService = async (service) => {
    // handles save for project-services AND project-subservices
    if (service.isPending) return;
    const missingFields: string[] = [];
    if (!service.name) missingFields.push("Name");
    if (!service.quantity) missingFields.push("Quantity");
    if (!service.hours) missingFields.push("Hours");
    if (missingFields.length) {
      setResponseMessage(
        `Please fill in required fields: ${missingFields.join(", ")}`
      );
      setShowFailAlert(true);
      return;
    }
    const isService = service.type === "project-services";
    const label = isService ? "service" : "subservice";

    let data = {
      id: Number(service.id),
      type: service.type,
      //@ts-ignore
      attributes: {
        name: service.name,
        quantity: service.quantity || 1,
        "override-hours": Number(service?.hours || 0),
        "service-description": service.description,
        languages: service?.languages || {},
      },
      relationships: {
        "project-resource": service?.resource
          ? {
              data: { id: service.resource.value, type: "project-resources" },
            }
          : null,
      },
    };

    // declare service relationships
    if (isService) {
      data.relationships["project"] = {
        data: { id: Number(project_id), type: "projects" },
      };

      if (service?.category)
        data.relationships["service-category"] = {
          data: {
            id: Number(service.category?.value),
            type: "service-categories",
          },
        };

      if (service?.location)
        data.relationships["project-location"] = {
          data: {
            id: Number(service.location?.value),
            type: "project-locations",
          },
        };

      if (!isManagedServices) {
        data.relationships["project-phase"] = {
          data: { id: Number(service.phase), type: "project-phases" },
        };
      }
    }

    try {
      const hook = isService ? updateService : updateSubservice;
      const response = await hook({
        slug: account_slug,
        id: data.id,
        //@ts-ignore
        body: { data },
      });

      //@ts-ignore
      const updated = response?.data?.data;
      if (!updated) {
        setResponseMessage(
          `Something went wrong updating this ${label}. ${
            //@ts-ignore
            response?.error?.data?.errors?.[0]?.detail || ""
          }`
        );
        setShowFailAlert(true);
      } else {
        setServices(
          services.map((s) => {
            if (isService) return s.id == updated?.id ? service : s;
            return {
              ...s,
              subservices: s.subservices?.map((sub) =>
                sub.id == updated?.id ? service : sub
              ),
            };
          })
        );
        setResponseMessage(`Project ${label} updated`);
        setShowSuccessAlert(true);
      }
    } catch (err: any) {
      setResponseMessage(
        `Something went wrong updating this ${label}. ${
          err?.data?.errors?.[0]?.detail || ""
        }`
      );
      setShowFailAlert(true);
    }
  };

  const manageProjectServicesPrivilege = hasPrivilege("projects.tasks", [
    "manage",
    "adjust_standards",
  ]);
  const manageStandardsPrivilege = hasPrivilege("projects.tasks", [
    "adjust_standards",
  ]);
  const manageCustomServicePrivilege = hasPrivilege("projects.custom_tasks", [
    "manage",
    "adjust_standards",
  ]);
  const manageSowLanguagePrivilege = hasPrivilege("projects.sow_language", [
    "manage",
  ]);
  const disableFields: boolean =
    !manageProjectServicesPrivilege || project?.status !== "building";
  const showLocationColumn = project && project.locations?.length > 1;
  const resourceOptions =
    renderResourceOptions(
      projectResources,
      resourceRatesEnabled ? resourceRates : resources,
      [],
      vendorResourceLabel
    )?.filter((opt) => opt.value !== -1) || [];

  const handleUpdatePending = (item) => {
    if (item.type == "project-services") {
      setServices(services.map((s) => (s.id === item.id ? item : s)));
    } else {
      setServices(
        services.map((s) =>
          s.subservices?.length
            ? {
                ...s,
                subservices: s.subservices.map((sub) =>
                  sub.id === item.id ? item : sub
                ),
              }
            : s
        )
      );
    }
  };

  const mainColumnDefs = [
    {
      id: "service name",
      className: showLocationColumn ? "sm" : "",
      header: () => "SERVICE NAME",
      cell(item) {
        const custom = item?.taskSource === "custom";
        return item.id !== "-1" ? (
          <div className="serviceName">
            <Form.Control
              className={`${custom ? "customItem" : ""}`}
              type="text"
              value={item.name}
              onChange={(e) => {
                const setName = (arr) =>
                  arr.map((s) =>
                    s.id === item.id ? { ...s, name: e.target.value } : s
                  );
                setServices(
                  item.type === "project-services"
                    ? setName(services)
                    : services.map((s) => {
                        return { ...s, subservices: setName(s.subservices) };
                      })
                );
              }}
              onBlur={async (e) =>
                await handleUpdateService({
                  ...item,
                  name: e.target.value,
                })
              }
              disabled={
                disableFields ||
                (!custom && !manageStandardsPrivilege) ||
                (custom && !manageCustomServicePrivilege)
              }
            />
            {!item?.type?.includes("subservices") && (
              <div className="previewService">
                {!item.isPending ? (
                  <FontAwesomeIcon
                    icon={faEye}
                    onClick={async () => {
                      setServiceToPreview({
                        ...item,
                        category: item?.category?.label || "",
                        phase: phases?.find((p) => p.id == item.phase),
                      });
                      setServiceSlideoutOpen(true);
                    }}
                  />
                ) : (
                  <div style={{ width: "16px" }}></div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginLeft: "10px" }}>{item.name}</div>
        );
      },
    },
    {
      id: "service category",
      className: showLocationColumn ? "sm" : "",
      header: () => "SERVICE CATEGORY",
      cell(item) {
        const custom = item?.taskSource === "custom";
        return (
          item?.id !== "-1" && (
            <Select
              className={`${item?.taskSource === "custom" ? "customItem" : ""}`}
              defaultValue={item.category}
              options={categories}
              onChange={async (e) => {
                const updated = { ...item, category: e };
                if (item?.isPending) {
                  handleUpdatePending(updated);
                  return;
                }
                if (e?.value !== item?.category?.value)
                  await handleUpdateService(updated);
              }}
              isDisabled={
                disableFields ||
                (!custom && !manageStandardsPrivilege) ||
                (custom && !manageCustomServicePrivilege)
              }
            />
          )
        );
      },
    },
    ...(showLocationColumn
      ? [
          {
            id: "location",
            header: () => "LOCATION",
            cell(item) {
              const custom = item?.taskSource === "custom";
              return (
                item?.id !== "-1" && (
                  <Select
                    className={`${
                      item?.taskSource === "custom" ? "customItem" : ""
                    }`}
                    defaultValue={item.location}
                    options={project.locations}
                    onChange={async (e) => {
                      const updated = { ...item, location: e };
                      if (item?.isPending) {
                        handleUpdatePending(updated);
                        return;
                      }
                      if (e?.value !== item?.location?.value)
                        await handleUpdateService(updated);
                    }}
                    isDisabled={
                      disableFields || (custom && !manageCustomServicePrivilege)
                    }
                  />
                )
              );
            },
          },
        ]
      : []),
    {
      id: "qty",
      header: () => "QTY",
      cell(item) {
        const custom = item?.taskSource === "custom";
        return (
          item.id !== "-1" && (
            <Form.Control
              className={`${item?.taskSource === "custom" ? "customItem" : ""}`}
              type="number"
              min="0"
              step="1"
              defaultValue={item.quantity}
              onChange={(e) => {
                if (item?.isPending) {
                  handleUpdatePending({ ...item, quantity: e.target.value });
                  return;
                }
              }}
              onBlur={async (e) => {
                if (e.target.value !== item.quantity.value)
                  await handleUpdateService({
                    ...item,
                    quantity: e.target.value,
                  });
              }}
              disabled={
                disableFields || (custom && !manageCustomServicePrivilege)
              }
            />
          )
        );
      },
    },
    {
      id: "hours",
      header: () => "HOURS",
      cell(item) {
        const custom = item?.taskSource === "custom";
        return (
          item.id !== "-1" && (
            <Form.Control
              className={`${item?.taskSource === "custom" ? "customItem" : ""}`}
              type="number"
              min="0"
              step="0.1"
              defaultValue={item.hours}
              onChange={(e) => {
                if (item?.isPending) {
                  handleUpdatePending({ ...item, hours: e.target.value });
                  return;
                }
              }}
              onBlur={async (e) => {
                if (e?.target?.value !== item.hours)
                  await handleUpdateService({ ...item, hours: e.target.value });
              }}
              disabled={
                disableFields || (custom && !manageCustomServicePrivilege)
              }
            />
          )
        );
      },
    },

    {
      id: "resource",
      header: () => "RESOURCE",
      cell(item) {
        const custom = item?.taskSource === "custom";
        return (
          item?.id !== "-1" && (
            <Select
              className="resourceDropdown"
              defaultValue={item.resource}
              options={resourceOptions}
              onChange={async (e) => {
                if (item?.isPending) {
                  handleUpdatePending({ ...item, resource: e });
                  return;
                }
                if (e?.value !== item?.resource?.value)
                  await handleUpdateService({ ...item, resource: e });
              }}
              isDisabled={
                disableFields || (custom && !manageCustomServicePrivilege)
              }
            />
          )
        );
      },
    },
  ];
  const serviceLanguageColumnDefs = [
    {
      id: "service name",
      header: () => "SERVICE NAME",
      cell(item) {
        return item.id !== "-1" ? (
          <div className="serviceName">
            <Form.Control
              key={item.id}
              className={`${item?.taskSource === "custom" ? "customItem" : ""}`}
              type="text"
              value={item.name}
              disabled
            />
            {!item?.type?.includes("subservices") && (
              <div className="previewService">
                <FontAwesomeIcon
                  icon={faEye}
                  onClick={() => {
                    setServiceToPreview({
                      ...item,
                      category: item?.category?.label || "",
                      phase: phases?.find((p) => p.id == item.phase),
                    });
                    setServiceSlideoutOpen(true);
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginLeft: "10px" }}>{item.name}</div>
        );
      },
    },
    {
      id: "servicelanguageitem",
      header() {
        return (
          <>
            {selectedLanguageField.label.toUpperCase()}
            <Tippy
              content={
                <span
                  style={{
                    textTransform: "none",
                    fontWeight: "400",
                    fontFamily: "Open Sans",
                  }}
                >
                  This content will appear in your documents, helping describe
                  the service that you are offering. You may include certain
                  project data in the description of your services. To learn
                  more{" "}
                  <a
                    href="https://support.scopestack.io/help/including-project-data-in-service-descriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline", color: "white" }}
                  >
                    see our help content
                  </a>
                  .
                </span>
              }
              interactive={true}
              placement="bottom"
              delay={[0, 200]}
            >
              <span style={{ marginLeft: 5, cursor: "pointer" }}>
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </Tippy>
          </>
        );
      },
      cell(item) {
        const field = selectedLanguageField.value;
        const value =
          field === "service-description"
            ? item.description
            : item.languages?.[field] || "";
        const custom = item?.taskSource === "custom";
        if (item.id === "-1") return <></>;
        const isDisabled =
          project?.status !== "building" ||
          !manageSowLanguagePrivilege ||
          (custom && !manageCustomServicePrivilege);
        const handleSave = async (markdown: string) => {
          await handleUpdateService(
            field === "service-description"
              ? { ...item, description: markdown }
              : {
                  ...item,
                  languages: {
                    ...item.languages,
                    [field]: markdown,
                  },
                }
          );
        };
        const editorKey = `${item.id}-${selectedLanguageField.value}`;
        const border = item?.taskSource === "custom" ? "#fcd22e" : "";
        return releaseTiptapEditor ? (
          <MarkdownTextAreaV2
            key={editorKey}
            value={value}
            placeholder={`Enter ${selectedLanguageField.label}`}
            disabled={isDisabled}
            onDebouncedChange={handleSave}
            borderColor={border}
          />
        ) : (
          <MarkdownTextArea
            key={editorKey}
            value={value}
            placeholder={`Enter ${selectedLanguageField.label}`}
            disabled={isDisabled}
            onDebouncedChange={handleSave}
            borderColor={border}
          />
        );
      },
    },
  ];

  const viewServicePricingPrivilege = hasPrivilege("projects.service_pricing", [
    "view",
    "manage",
  ]);
  const manageServicePricingPrivilege = hasPrivilege(
    "projects.service_pricing",
    ["manage"]
  );

  const handleUpdateServicePricing = async (item) => {
    const isService = item.type === "project-services";
    const label = isService ? "service" : "subservice";

    let data: any = {
      id: Number(item.id),
      type: item.type,
      attributes: {
        quantity: Number(item.quantity || 1),
        "override-hours": Number(item.hours || 0),
        "unit-price": Number(item.unitPrice || 0),
        "unit-cost": Number(item.unitCost || 0),
      },
    };

    if (isService) {
      data.relationships = {
        project: {
          data: { id: Number(project_id), type: "projects" },
        },
      };
    }

    try {
      const hook = isService ? updateService : updateSubservice;
      const response = await hook({
        slug: account_slug,
        id: data.id,
        //@ts-ignore
        body: { data },
      });

      //@ts-ignore
      const updated = response?.data?.data;
      if (!updated) {
        setResponseMessage(
          `Something went wrong updating this ${label}. ${
            //@ts-ignore
            response?.error?.data?.errors?.[0]?.detail || ""
          }`
        );
        setShowFailAlert(true);
      } else {
        refetchServices();
        setResponseMessage(`Pricing updated`);
        setShowSuccessAlert(true);
      }
    } catch (err: any) {
      setResponseMessage(
        `Something went wrong updating pricing. ${
          err?.data?.errors?.[0]?.detail || ""
        }`
      );
      setShowFailAlert(true);
    }
  };

  const pricingDisabled =
    !manageServicePricingPrivilege || project?.status !== "building";

  const servicePricingColumnDefs = [
    {
      id: "service name",
      header: () => "SERVICE",
      cell(item) {
        return item.id !== "-1" ? (
          <Form.Control type="text" value={item.name} disabled />
        ) : (
          <div style={{ marginLeft: "10px" }}>{item.name}</div>
        );
      },
    },
    {
      id: "quantity",
      header: () => "QTY",
      cell(item) {
        const readOnly = pricingDisabled || item.taskSource === "third_party";
        return (
          item.id !== "-1" && (
            <Form.Control
              type="number"
              min="0"
              step="1"
              defaultValue={item.quantity}
              onBlur={async (e) => {
                if (e.target.value !== item.quantity)
                  await handleUpdateServicePricing({
                    ...item,
                    quantity: e.target.value,
                  });
              }}
              disabled={readOnly}
            />
          )
        );
      },
    },
    {
      id: "hours",
      header: () => "LOE",
      cell(item) {
        const readOnly = pricingDisabled || item.taskSource === "third_party";
        return (
          item.id !== "-1" && (
            <Form.Control
              type="number"
              min="0"
              step="0.0001"
              defaultValue={item.hours}
              onBlur={async (e) => {
                if (e.target.value !== item.hours) {
                  const newHours = Number(e.target.value);
                  const rate = Number(item.hourlyRate || 0);
                  const costRate = Number(item.hourlyCost || 0);
                  await handleUpdateServicePricing({
                    ...item,
                    hours: e.target.value,
                    unitPrice: String(Math.round(newHours * rate * 100) / 100),
                    unitCost: String(
                      Math.round(newHours * costRate * 100) / 100
                    ),
                  });
                }
              }}
              disabled={readOnly}
            />
          )
        );
      },
    },
    {
      id: "hourlyRate",
      header: () => "LOE RATE",
      cell(item) {
        return (
          item.id !== "-1" && (
            <Form.Control
              type="number"
              step="0.01"
              defaultValue={item.hourlyRate}
              disabled
            />
          )
        );
      },
    },
    {
      id: "unitPrice",
      header: () => "UNIT PRICE",
      cell(item) {
        const readOnly = pricingDisabled || item.taskSource === "third_party";
        return (
          item.id !== "-1" && (
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              defaultValue={item.unitPrice}
              onBlur={async (e) => {
                if (e.target.value !== item.unitPrice)
                  await handleUpdateServicePricing({
                    ...item,
                    unitPrice: e.target.value,
                  });
              }}
              disabled={readOnly}
            />
          )
        );
      },
    },
    {
      id: "hourlyCost",
      header: () => "LOE COST",
      cell(item) {
        return (
          item.id !== "-1" && (
            <Form.Control
              type="number"
              step="0.01"
              defaultValue={item.hourlyCost}
              disabled
            />
          )
        );
      },
    },
    {
      id: "unitCost",
      header: () => "UNIT COST",
      cell(item) {
        const readOnly = pricingDisabled || item.taskSource === "third_party";
        return (
          item.id !== "-1" && (
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              defaultValue={item.unitCost}
              onBlur={async (e) => {
                if (e.target.value !== item.unitCost)
                  await handleUpdateServicePricing({
                    ...item,
                    unitCost: e.target.value,
                  });
              }}
              disabled={readOnly}
            />
          )
        );
      },
    },
  ];

  const getDeleteServicesMessage = () => {
    const services = selectedServices.filter(
      (s) => s.type === "project-services"
    );
    const subservices = selectedServices.filter(
      (s) => s.type === "project-subservices"
    );

    const totalSubservices = services.reduce(
      (count, s) =>
        count +
        (("subservices" in s &&
          Array.isArray((s as ProjectService).subservices) &&
          (s as ProjectService).subservices.length) ||
          0),
      0
    );

    return (
      <div>
        <div>
          You are about to delete {services.length} service
          {services.length > 1 ? "s" : ""}
          {totalSubservices > 0 &&
            ` with ${totalSubservices} subservice${
              totalSubservices > 1 ? "s" : ""
            }`}
        </div>
        <div style={{ paddingTop: "10px" }}>
          {services.length && subservices.length ? (
            <>
              <strong>Services</strong>
              <ul>
                {services.map((s) => {
                  return <li key={s.id}>{s.name}</li>;
                })}
              </ul>
              <strong>Subservices</strong>
              <ul>
                {subservices.map((sub) => {
                  return <li key={sub.id}>{sub.name}</li>;
                })}
              </ul>
            </>
          ) : (
            <ul>
              {selectedServices.map((s) => {
                return <li key={s.id}>{s.name}</li>;
              })}
            </ul>
          )}
        </div>
        <div>Are you sure you want to continue?</div>
      </div>
    );
  };
  const handleCreateVendorResource = async () => {
    if (!newResource.vendor || !newResource.name) {
      setResponseMessage(
        `${!newResource.vendor ? "Vendor" : "Resource name"} is required`
      );
      setShowFailAlert(true);
      return;
    }

    const data = {
      type: "project-resources",
      attributes: {
        name: newResource.vendor.label || "",
        "external-name": newResource.name,
        "hourly-cost": parseFloat(newResource.cost || ""),
        "hourly-rate": parseFloat(newResource.price || ""),
        resource: {
          resource_id: Number(newResource.vendor.value),
          resource_type: "vendors",
        },
      },
      relationships: {
        project: { data: { id: Number(project_id), type: "projects" } },
      },
    };

    const response = await createVendorResource({
      slug: account_slug,
      body: { data },
    });

    //@ts-ignore
    if (response?.data?.data?.id) {
      refetchProjectResources();
      setResponseMessage(`${vendorResourceLabel} added`);
      setShowSuccessAlert(true);
      setNewResource({
        vendor: undefined,
        name: "",
        cost: "0.00",
        margin: "25",
        price: "0.00",
      });
      setSlideoutStack([]);
    } else {
      setResponseMessage(`Error creating ${vendorResourceLabel}`);
      setShowFailAlert(true);
    }
  };
  const isLoading =
    servicesLoading || phasesLoading || projectLoading || privilegesLoading;
  const isManagedServices = service_type === "managed_services";
  const building = project?.status === "building";
  const showAllSubservices = fetchedServices.every(
    (s) => !s.subservices?.length || expanded.services[s.id]
  );
  return (
    <div className="projectServicesV2">
      <div className="projectServicesHeader">
        <div className="title">{snakeToText(service_type)}</div>
        <div className="actionBtns">
          {!isLoading && (
            <Button
              className="btnSeawhite"
              variant="secondary"
              onClick={() =>
                showAllSubservices
                  ? toggleSubservices(false)
                  : toggleSubservices(true)
              }
            >
              {`${showAllSubservices ? "Hide" : "Show"} all Subservices`}
            </Button>
          )}
          {hasPrivilege("projects.resource_rates", ["manage", "create"]) && (
            <Button
              className="btnSeawhite"
              variant="secondary"
              onClick={() => setSlideoutStack(["addResource"])}
            >
              {`Add ${vendorResourceLabel}`}
            </Button>
          )}
          {hasPrivilege("projects.project_phases", ["manage", "create"]) &&
            building &&
            !isManagedServices && (
              <Button
                className="btnSeawhite"
                variant="secondary"
                onClick={(e) => setShowAddPhaseModal(true)}
              >
                Add Phase
              </Button>
            )}
          {hasPrivilege("projects.tasks", [
            "manage",
            "create",
            "adjust_standards",
          ]) &&
            building && (
              <Button
                className="btnSeafoam"
                onClick={(e) => {
                  setShowAddServicesModal(true);
                }}
                style={{ border: "2px solid #418172" }}
              >
                Add a Service
              </Button>
            )}
        </div>
      </div>
      <Tab.Container
        activeKey={activeTab}
        onSelect={(eventKey) => {
          if (
            hasPendingItems &&
            !window.confirm(
              "You have unsaved services. Are you sure you want to leave?"
            )
          ) {
            return;
          }
          refetchServices();
          setActiveTab(eventKey || "1");
        }}
      >
        <div className="tabsHeaderRow">
          {selectedServices.length > 0 && (
            <button
              type="button"
              className="selectedBtn"
              onClick={() => setShowDeleteServicesModal(true)}
            >
              <div className="selectedMsg">
                {selectedServices.length} Selected
              </div>
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          )}

          <Nav variant="tabs" className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="1" className="projectServicesTabNav">
                Services
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="2" className="projectServicesTabNav">
                Service Language
              </Nav.Link>
            </Nav.Item>
            {configServicePricingEnabled && viewServicePricingPrivilege && (
              <Nav.Item>
                <Nav.Link eventKey="3" className="projectServicesTabNav">
                  Service Pricing
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </div>

        <Tab.Content>
          <Tab.Pane eventKey="1" className="projectServicesTabContent">
            {isLoading ? (
              <ScopeStackSpinner />
            ) : (
              <ServiceEditorV2
                services={services}
                setServices={setServices}
                phases={phases}
                setPhases={setPhases}
                prevPhasesRef={prevPhasesRef}
                prevServicesRef={prevServicesRef}
                refetchServices={refetchServices}
                refetchPhases={refetchPhases}
                columnDefs={mainColumnDefs}
                draggable={true}
                selectable={true}
                service_type={service_type}
                showNotes={true}
                showSettings={true}
                project={project}
                expanded={expanded}
                setExpanded={setExpanded}
              />
            )}
          </Tab.Pane>
          <Tab.Pane
            eventKey="2"
            className="projectServicesTabContent  serviceLanguageTab"
          >
            {isLoading ? (
              <ScopeStackSpinner />
            ) : (
              <>
                <div className="languageFieldSelect">
                  <div className="lfLabel">Show / edit content for:</div>
                  <Select
                    onChange={(e) => {
                      setSelectedLanguageField(e!);
                      refetchServices();
                    }}
                    className="basic-single"
                    value={selectedLanguageField}
                    options={[
                      {
                        label: "Service Description",
                        value: "service-description",
                      },
                      ...fields,
                    ]}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                  />
                </div>
                <ServiceEditorV2
                  services={services}
                  setServices={setServices}
                  phases={phases}
                  setPhases={setPhases}
                  prevPhasesRef={prevPhasesRef}
                  prevServicesRef={prevServicesRef}
                  refetchServices={refetchServices}
                  refetchPhases={refetchPhases}
                  columnDefs={serviceLanguageColumnDefs}
                  draggable={false}
                  selectable={false}
                  service_type={service_type}
                  showNotes={true}
                  showSettings={false}
                  project={project}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              </>
            )}
          </Tab.Pane>
          {configServicePricingEnabled && viewServicePricingPrivilege && (
            <Tab.Pane
              eventKey="3"
              className="projectServicesTabContent servicePricingTab"
            >
              {isLoading ? (
                <ScopeStackSpinner />
              ) : (
                <ServiceEditorV2
                  services={services}
                  setServices={setServices}
                  phases={phases}
                  setPhases={setPhases}
                  prevPhasesRef={prevPhasesRef}
                  prevServicesRef={prevServicesRef}
                  refetchServices={refetchServices}
                  refetchPhases={refetchPhases}
                  columnDefs={servicePricingColumnDefs}
                  draggable={false}
                  selectable={false}
                  service_type={service_type}
                  showNotes={false}
                  showSettings={false}
                  project={project}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              )}
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>

      {/* MODALS */}
      {/* Add Service Modal */}
      <ScopeStackModal
        modalTitle="Add a Service"
        modalBody={
          <div>
            <div
              className="addServiceOption"
              onClick={() =>
                navigate(`/projects/${project_id}/project_surveys/take`)
              }
            >
              <FontAwesomeIcon icon={faCirclePlus} /> Add Services from Survey
            </div>
            <div
              className="addServiceOption"
              onClick={() =>
                navigate(`/projects/${project_id}/task_blueprints/new`)
              }
            >
              <FontAwesomeIcon icon={faCirclePlus} /> Add Services from
              Blueprint
            </div>
            <div
              className="addServiceOption"
              onClick={() =>
                navigate(
                  `/projects/${project_id}/standard_tasks/new?service_type=${service_type}`
                )
              }
            >
              <FontAwesomeIcon icon={faCirclePlus} /> Add from Standard Services
            </div>
          </div>
        }
        handleClose={() => setShowAddServicesModal(false)}
        show={showAddServicesModal}
        button1Text={undefined}
        button2Text={undefined}
        handleButton1Click={() => null}
        handleButton2Click={() => null}
        className="addServicesModal"
      />
      {/* Add Phase Modal */}
      <ScopeStackModal
        modalTitle="Create Custom Phase"
        modalBody={
          <div>
            <div style={{ marginBottom: "15px" }}>
              Enter a name for the phase you are about to create.
            </div>
            <TextField
              id="phaseName"
              label="Name"
              placeholder="Enter a name for this phase"
              onChange={(e) => setNewPhaseName(e.target.value)}
              required
            />
          </div>
        }
        handleClose={closeModal}
        show={showAddPhaseModal}
        button1Text="Cancel"
        handleButton1Click={closeModal}
        button2Text="Create Phase"
        handleButton2Click={async () => {
          const data = {
            type: "project-phases",
            attributes: { name: newPhaseName },
            relationships: {
              project: { data: { id: project_id, type: "projects" } },
            },
          };

          const response = await createPhase({
            slug: account_slug,
            //@ts-ignore
            body: { data },
          });

          //@ts-ignore
          if (response?.data?.data?.id) {
            refetchPhases();
            closeModal();
            setResponseMessage("Custom phase added");
            setShowSuccessAlert(true);
          } else {
            setResponseMessage("Error creating custom phase");
            setShowFailAlert(true);
          }
        }}
        button2Disabled={!newPhaseName}
        className="projectPhaseModal"
      />
      {/* Rename Phase Modal */}
      <ScopeStackModal
        modalTitle="Rename Phase"
        modalBody={
          <div>
            <div style={{ marginBottom: "15px" }}>
              Please provide the new phase name
            </div>
            <TextField
              id="phaseName"
              label="Phase Name"
              defaultValue={phases?.find((p) => p.id === openPhaseMenuId)?.name}
              placeholder="Enter a name for this phase"
              onChange={(e) => setNewPhaseName(e.target.value)}
              required
            />
          </div>
        }
        handleClose={closeModal}
        show={showRenamePhaseModal}
        button1Text="Cancel"
        handleButton1Click={closeModal}
        button2Text="Rename"
        handleButton2Click={async () => {
          const data = {
            id: Number(openPhaseMenuId),
            type: "project-phases",
            attributes: { name: newPhaseName },
          };

          const response = await updatePhase({
            slug: account_slug,
            id: Number(openPhaseMenuId),
            //@ts-ignore
            body: { data },
          });

          //@ts-ignore
          if (response?.data?.data?.id) {
            refetchPhases();
            closeModal();
            setResponseMessage("Phase renamed");
            setShowSuccessAlert(true);
          } else {
            setResponseMessage("Error saving phase name");
            setShowFailAlert(true);
          }
        }}
        button2Disabled={!newPhaseName}
        className="projectPhaseModal"
      />
      {/* Delete Phase Confimation */}
      <ScopeStackModal
        modalTitle="Delete Phase"
        modalBody="Are you sure you want to delete this phase?"
        button1Text="Cancel"
        handleButton1Click={closeModal}
        button2Text={"Delete"}
        handleButton2Click={async () => {
          const response = await deletePhase({
            slug: account_slug,
            id: Number(openPhaseMenuId),
          });

          //@ts-ignore
          if (response?.error) {
            setResponseMessage("Error deleting phase");
            setShowFailAlert(true);
          } else {
            refetchPhases();
            closeModal();
            setResponseMessage("Phase deleted");
            setShowSuccessAlert(true);
          }
        }}
        show={showDeletePhaseModal}
        handleClose={closeModal}
        deleteModal={true}
      />
      {/* Delete Service Confimation */}
      <ScopeStackModal
        modalTitle={`Delete Service${selectedServices.length > 1 ? "s" : ""}`}
        modalBody={getDeleteServicesMessage()}
        button1Text="Cancel"
        handleButton1Click={closeModal}
        button2Text={"Delete"}
        handleButton2Click={async () => {
          if (!selectedServices.length) return;

          const validTypes = ["project-services", "project-subservices"];
          const valid = selectedServices.filter((s) =>
            validTypes.includes(s?.type || "")
          );
          const errors: (ProjectService | ProjectSubservice)[] = [];
          for (const service of valid) {
            const hook =
              service.type === "project-services"
                ? deleteService
                : deleteSubservice;
            const res = await hook({
              slug: account_slug,
              id: Number(service.id),
            });
            //@ts-ignore
            if (res?.error) errors.push(service);
          }

          setResponseMessage(
            errors.length
              ? "Unsuccesfully deleted service(s)"
              : "Service(s) deleted"
          );
          if (errors.length) {
            setShowFailAlert(true);
          } else {
            setShowSuccessAlert(true);
          }

          refetchServices();
          closeModal();
        }}
        show={showDeleteServicesModal}
        handleClose={closeModal}
        deleteModal={true}
      />
      {/* ADD VENDOR RESOURCE SLIDEOUT */}
      <SlideOutMenuV2
        className="addVendorResourceSlideout"
        isOpen={slideoutStack.includes("addResource")}
        onClose={() => {
          setNewResource({
            vendor: undefined,
            name: "",
            cost: "0.00",
            margin: "25",
            price: "0.00",
          });
          setSlideoutStack([]);
        }}
        onSave={handleCreateVendorResource}
        title={`Add ${vendorResourceLabel}`}
        actionBtnText="Add"
      >
        <div>
          <Row style={{ alignItems: "center" }}>
            <Col>
              <Form.Group>
                <Form.Label>Which vendor will supply the resource?</Form.Label>
                <Select
                  value={
                    vendors.find(
                      (v) => v.value === newResource?.vendor?.value
                    ) || null
                  }
                  options={vendors}
                  onChange={(e) => {
                    if (e) setNewResource({ ...newResource, vendor: e });
                  }}
                  className="formGroup"
                />
              </Form.Group>
            </Col>
            {hasPrivilege("settings.vendors", ["create", "manage"]) && (
              <Col sm={3} style={{ textAlign: "center" }}>
                <Button
                  className="btnSeawhite"
                  variant="secondary"
                  onClick={() => setSlideoutStack(["addResource", "addVendor"])}
                >
                  Add Vendor
                </Button>
              </Col>
            )}
          </Row>
          <TextField
            id="resourceName"
            label="Resource Name"
            placeholder="Enter resource name"
            value={newResource.name}
            onChange={(e) =>
              setNewResource({ ...newResource, name: e.target.value })
            }
          />
          <Row>
            <Col>
              <Form.Group>
                <Form.Label>Hourly Cost</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.1"
                  onChange={(e) => {
                    const cost = parseFloat(e.target.value) || 0;
                    const price = calculatePrice(
                      cost,
                      newResource.margin,
                      configVendorQuoteRevenueCalculation
                    );
                    setNewResource({
                      ...newResource,
                      cost: String(cost),
                      price: String(price),
                    });
                  }}
                  value={newResource.cost}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Margin (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.0001"
                  onChange={(e) => {
                    const margin = parseFloat(e.target.value) || 0;
                    const price = calculatePrice(
                      newResource.cost,
                      margin,
                      configVendorQuoteRevenueCalculation
                    );
                    setNewResource({
                      ...newResource,
                      margin: String(margin),
                      price: String(price),
                    });
                  }}
                  value={newResource.margin}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Hourly Rate</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  onChange={(e) => {
                    const price = parseFloat(e.target.value) || 0;
                    const margin = calculateMarkup(
                      newResource.cost,
                      price,
                      configVendorQuoteRevenueCalculation
                    );
                    setNewResource({
                      ...newResource,
                      margin: String(margin),
                      price: String(price),
                    });
                  }}
                  value={newResource.price}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </SlideOutMenuV2>
      {/* ADD VENDOR SLIDEOUT (STACKED ON TOP) */}
      <AddVendorSlideout
        slideoutOpen={slideoutStack[slideoutStack.length - 1] === "addVendor"}
        setSlideoutOpen={(open: boolean) => {
          if (!open) setSlideoutStack(["addResource"]);
        }}
        setNewVendor={(vendor) => {
          setNewResource((prev) => ({ ...prev, vendor }));
        }}
        refetch={refetchVendors}
      />
      {serviceToPreview && (
        <ServicePreview
          service={serviceToPreview}
          slideoutOpen={serviceSlideoutOpen}
          setSlideoutOpen={setServiceSlideoutOpen}
          isManagedService={isManagedServices}
        />
      )}
      {handleAlert(
        showFailAlert,
        responseMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        responseMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
    </div>
  );
};

export default Show;
